import { Outlet, useOutletContext } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete, Link, Upload } from '@factorialco/f0-react/icons/app'
import {
  TrainingDetailAttachmentsDeleteTrainingFileDocument,
  TrainingDetailAttachmentsDeleteTrainingLinkDocument,
  TrainingDetailMaterialsDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import {
  MaterialItem,
  MaterialsList,
  getSortableTitle,
} from 'modules/trainings/components/MaterialsList'
import { DetailOutletContext } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

export default function TrainingMaterials() {
  const { training } = useOutletContext<DetailOutletContext>()

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const canEdit = training._policies?.edit.can ?? false

  const [destroyTrainingFile] = useMutation(TrainingDetailAttachmentsDeleteTrainingFileDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingFile,
    awaitRefetchQueries: true,
    refetchQueries: [TrainingDetailMaterialsDocument],
  })

  const [deleteTrainingLink] = useMutation(TrainingDetailAttachmentsDeleteTrainingLinkDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingLink,
    awaitRefetchQueries: true,
    refetchQueries: [TrainingDetailMaterialsDocument],
  })

  const source = useGraphqlDataCollectionSource({
    document: TrainingDetailMaterialsDocument,
    search: {
      enabled: true,
    },
    mapData: (data): MaterialItem[] => {
      const files = data.trainings.trainingFilesConnection.nodes.map((file) => ({
        contentType: file.contentType,
        filename: file.filename,
        id: file.id,
        title: file.title,
        type: 'training' as const,
        size: file.size,
      }))
      const links = data.trainings.trainingLinksConnection.nodes.map((link) => ({
        title: link.title,
        id: link.id,
        url: link.url,
        type: 'link' as const,
      }))

      return [...files, ...links].sort((a, b) =>
        getSortableTitle(a) > getSortableTitle(b) ? 1 : -1
      )
    },
    mapVariables: ({ search }) => ({
      trainingId: training.id,
      search: search,
    }),
    mapPagination: (data) => {
      const filesCount = data.trainings.trainingFilesConnection.totalCount || 0
      const linksCount = data.trainings.trainingLinksConnection.totalCount || 0
      return {
        total: filesCount + linksCount,
      }
    },
    itemUrl: (item) => {
      switch (item.type) {
        case 'link': {
          return item.url
        }
        default: {
          return navigation.trainings.list.details_revamp.view.attachments.previewFile.fullPath({
            id: training.id,
            fileId: item.id,
          })
        }
      }
    },
    primaryActions: () => ({
      label: i18n.t('common.upload'),
      icon: Upload,
      disabled: !canEdit,
      onClick: () => {
        navigate(
          navigation.trainings.list.details_revamp.view.attachments.newFile.fullPath({
            id: training.id,
          })
        )
      },
    }),
    secondaryActions: {
      expanded: 1,
      actions: () => [
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.materials.new_link'),
          icon: Link,
          onClick: () => {
            navigate(
              navigation.trainings.list.details_revamp.view.attachments.newLink.fullPath({
                id: training.id,
              })
            )
          },
        },
      ],
    },
    itemActions: (item) => {
      if (!canEdit) return []
      return [
        {
          label: i18n.t('common.delete'),
          onClick: () => {
            switch (item.type) {
              case 'link': {
                deleteTrainingLink({
                  variables: { id: item.id },
                })
                break
              }
              default: {
                destroyTrainingFile({
                  variables: { id: item.id },
                })
              }
            }
          },
          icon: Delete,
        },
      ]
    },
  })

  return (
    <ResponsivePage.Body>
      <Box gap="s16">
        <SectionHeader
          title={i18n.t(
            'trainings.trainings_revamp.training_detail.materials.section_header.title'
          )}
          description={i18n.t(
            'trainings.trainings_revamp.training_detail.materials.section_header.description'
          )}
          separator="bottom"
        />
        <MaterialsList
          source={source}
          emptyStates={{
            'no-data': {
              title: i18n.t(
                'trainings.trainings_revamp.training_detail.materials.datacollection.empty_state.no_data.title'
              ),
              description: i18n.t(
                'trainings.trainings_revamp.training_detail.materials.datacollection.empty_state.no_data.description'
              ),
            },
          }}
        />
        <Outlet />
      </Box>
    </ResponsivePage.Body>
  )
}
